import { useEffect, useMemo, useState } from "react";
import {
  AlertOctagon,
  BadgeCheck,
  Boxes,
  CheckCircle2,
  CircleDashed,
  Clock3,
  Cog,
  DollarSign,
  MapPin,
  PackageSearch,
  Play,
  Save,
  Timer,
  Wrench,
  X,
} from "lucide-react";
import { listWorkshopOrders, updateWorkshopOrder } from "../api";
import type { AuthUser, Vehicle, WorkshopOrder } from "../types";

type Filter = "active" | "blocking" | "waiting_parts" | "completed";

const stageLabel = {
  scheduled: "PROGRAMADA",
  in_progress: "EN REPARACIÓN",
  waiting_parts: "ESPERA REFACCIÓN",
  quality_check: "CONTROL DE CALIDAD",
  completed: "LIBERADA",
  cancelled: "CANCELADA",
};

const nextActions: Partial<
  Record<WorkshopOrder["stage"], [WorkshopOrder["stage"], string]>
> = {
  scheduled: ["in_progress", "INICIAR REPARACIÓN"],
  in_progress: ["quality_check", "ENVIAR A CALIDAD"],
  waiting_parts: ["in_progress", "REANUDAR TRABAJO"],
  quality_check: ["completed", "LIBERAR UNIDAD"],
};

const money = (value: number) =>
  new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    maximumFractionDigits: 0,
  }).format(value);

export function WorkshopPanel({
  open,
  onClose,
  vehicles,
  user,
  onSelectVehicle,
}: {
  open: boolean;
  onClose: () => void;
  vehicles: Vehicle[];
  user: AuthUser;
  onSelectVehicle: (vehicle: Vehicle) => void;
}) {
  const [rows, setRows] = useState<WorkshopOrder[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [filter, setFilter] = useState<Filter>("active");
  const [technician, setTechnician] = useState("");
  const [workshop, setWorkshop] = useState("");
  const [notes, setNotes] = useState("");
  const [actualCost, setActualCost] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");

  function refresh() {
    listWorkshopOrders()
      .then((items) => {
        setRows(items);
        setSelectedId((current) =>
          items.some((item) => item.execution_id === current)
            ? current
            : items[0]?.execution_id ?? null,
        );
        setError("");
      })
      .catch((cause) =>
        setError(cause instanceof Error ? cause.message : "No fue posible cargar taller"),
      );
  }

  useEffect(() => {
    if (!open) return;
    refresh();
    window.addEventListener("gsf:workshop-updated", refresh);
    return () => window.removeEventListener("gsf:workshop-updated", refresh);
  }, [open]);

  const visibleRows = useMemo(
    () =>
      rows.filter((row) => {
        if (filter === "active") return !["completed", "cancelled"].includes(row.stage);
        if (filter === "blocking") return row.blocking;
        if (filter === "waiting_parts") return row.stage === "waiting_parts";
        return ["completed", "cancelled"].includes(row.stage);
      }),
    [filter, rows],
  );
  const selected =
    visibleRows.find((row) => row.execution_id === selectedId) || visibleRows[0] || rows[0];
  const canManage =
    user.permissions?.includes("maintenance.manage") ??
    ["superadmin", "fleet_admin", "maintenance"].includes(user.role);
  const summary = useMemo(
    () => ({
      active: rows.filter((row) => !["completed", "cancelled"].includes(row.stage)).length,
      blocking: rows.filter((row) => row.blocking).length,
      waiting: rows.filter((row) => row.stage === "waiting_parts").length,
      completed: rows.filter((row) => row.stage === "completed").length,
    }),
    [rows],
  );

  function selectOrder(row: WorkshopOrder) {
    setSelectedId(row.execution_id);
    setTechnician(row.technician_name);
    setWorkshop(row.workshop_name);
    setNotes(row.notes);
    setActualCost(String(row.actual_cost || ""));
    setError("");
  }

  async function applyUpdate(payload: Parameters<typeof updateWorkshopOrder>[1]) {
    if (!selected) return;
    setPending(true);
    setError("");
    try {
      const updated = await updateWorkshopOrder(selected.maintenance_order_id, payload);
      setRows((current) =>
        current.map((row) => row.execution_id === updated.execution_id ? updated : row),
      );
      selectOrder(updated);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "No fue posible actualizar la orden");
    } finally {
      setPending(false);
    }
  }

  async function updateTask(taskId: number, completed: boolean) {
    await applyUpdate({
      task_id: taskId,
      task_status: completed ? "pending" : "completed",
      event_note: completed ? "Tarea reabierta para corrección." : "Tarea verificada y completada.",
    });
  }

  const nextAction = selected ? nextActions[selected.stage] : undefined;

  if (!open) return null;
  return (
    <div className="admin-overlay" role="presentation" onMouseDown={onClose}>
      <aside className="admin-panel workshop-panel" role="dialog" aria-modal="true" aria-labelledby="workshop-title" onMouseDown={(event) => event.stopPropagation()}>
        <header>
          <div>
            <span className="eyebrow">MANTENIMIENTO · EJECUCIÓN DE TALLER</span>
            <h2 id="workshop-title">Órdenes de trabajo</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar taller"><X size={18} /></button>
        </header>
        <section className="admin-panel__summary">
          <span><Wrench size={17} /><b>{summary.active}</b> activas</span>
          <span><AlertOctagon size={17} /><b>{summary.blocking}</b> bloqueantes</span>
          <span><PackageSearch size={17} /><b>{summary.waiting}</b> esperando parte</span>
          <span><BadgeCheck size={17} /><b>{summary.completed}</b> liberadas</span>
        </section>
        <div className="workshop-body">
          <section className="workshop-queue">
            <div className="workshop-filters">
              {(["active", "blocking", "waiting_parts", "completed"] as Filter[]).map((item) => (
                <button type="button" className={filter === item ? "is-active" : ""} onClick={() => setFilter(item)} key={item}>
                  {{ active: "ACTIVAS", blocking: "BLOQUEANTES", waiting_parts: "REFACCIONES", completed: "HISTÓRICO" }[item]}
                </button>
              ))}
            </div>
            <div className="workshop-list">
              {visibleRows.map((row) => (
                <button type="button" className={`workshop-item workshop-item--${row.priority}${row.blocking ? " is-blocking" : ""}${selected?.execution_id === row.execution_id ? " is-selected" : ""}`} onClick={() => selectOrder(row)} key={row.execution_id}>
                  <span>{row.blocking ? <AlertOctagon size={16} /> : <Cog size={16} />}</span>
                  <span><small>{row.work_order_number} · {row.vehicle_unit_code}</small><strong>{row.title}</strong><em>{row.technician_name || "Sin técnico"} · {stageLabel[row.stage]}</em></span>
                  <b>{row.progress_percent}%</b>
                  <i><em style={{ width: `${row.progress_percent}%` }} /></i>
                </button>
              ))}
              {!visibleRows.length && <p className="maintenance-empty">SIN ÓRDENES EN ESTA VISTA</p>}
            </div>
          </section>
          <section className="workshop-detail">
            {selected && (
              <>
                <div className={`workshop-stage workshop-stage--${selected.stage}${selected.blocking ? " is-blocking" : ""}`}>
                  {selected.blocking ? <AlertOctagon size={34} /> : selected.stage === "completed" ? <BadgeCheck size={34} /> : <Wrench size={34} />}
                  <span><small>{selected.blocking ? "NO LIBERAR" : "ETAPA ACTUAL"}</small><b>{stageLabel[selected.stage]}</b></span>
                </div>
                <div className="workshop-title">
                  <span className="eyebrow">{selected.work_order_number} · {selected.vehicle_unit_code}</span>
                  <h3>{selected.title}</h3>
                  <p>{selected.vehicle_name} · {selected.component_code || "componente general"}</p>
                </div>
                <div className="workshop-kpis">
                  <article><Timer size={15} /><span><small>TIEMPO MUERTO</small><strong>{selected.downtime_hours} h</strong></span></article>
                  <article><DollarSign size={15} /><span><small>ESTIMADO / REAL</small><strong>{money(selected.estimated_cost)} / {money(selected.actual_cost)}</strong></span></article>
                  <article><Boxes size={15} /><span><small>REFACCIONES</small><strong>{money(selected.parts_total)}</strong></span></article>
                </div>
                <div className="workshop-tasks">
                  <span className="eyebrow">PROTOCOLO DE TRABAJO</span>
                  {selected.tasks.map((task) => (
                    <button type="button" disabled={!canManage || pending} className={task.status === "completed" ? "is-complete" : task.status === "in_progress" ? "is-progress" : ""} onClick={() => updateTask(task.id, task.status === "completed")} key={task.id}>
                      {task.status === "completed" ? <CheckCircle2 size={15} /> : <CircleDashed size={15} />}
                      <span><strong>{task.label}</strong><small>{task.task_code} · {task.required ? "obligatoria" : "opcional"}</small></span>
                      <b>{task.status}</b>
                    </button>
                  ))}
                </div>
                <div className="workshop-parts">
                  <span className="eyebrow">REFACCIONES Y CONSUMIBLES</span>
                  {selected.parts.map((part) => (
                    <article key={part.id}><PackageSearch size={14} /><span><small>{part.part_number} · {part.status}</small><strong>{part.description}</strong><em>{part.supplier_name}</em></span><b>{part.quantity} × {money(part.unit_cost)}</b></article>
                  ))}
                </div>
                <div className="workshop-assignment">
                  <label>TÉCNICO<input disabled={!canManage} value={technician || selected.technician_name} onChange={(event) => setTechnician(event.target.value)} /></label>
                  <label>TALLER / PROVEEDOR<input disabled={!canManage} value={workshop || selected.workshop_name} onChange={(event) => setWorkshop(event.target.value)} /></label>
                  <label>COSTO REAL<input type="number" min="0" disabled={!canManage} value={actualCost || selected.actual_cost || ""} onChange={(event) => setActualCost(event.target.value)} /></label>
                  <label>NOTAS<textarea disabled={!canManage} value={notes || selected.notes} onChange={(event) => setNotes(event.target.value)} /></label>
                </div>
                <div className="workshop-events">
                  <span className="eyebrow">BITÁCORA</span>
                  {selected.events.slice(0, 5).map((event) => (
                    <article key={event.id}><Clock3 size={13} /><span><strong>{event.note}</strong><small>{event.event_type} · {new Date(event.created_at).toLocaleString("es-MX")}</small></span></article>
                  ))}
                </div>
                {error && <p className="admin-form-message is-error">{error}</p>}
                <div className="workshop-actions">
                  <button type="button" onClick={() => {
                    const vehicle = vehicles.find((item) => item.id === selected.vehicle_id);
                    if (vehicle) onSelectVehicle(vehicle);
                  }}><MapPin size={13} /> VER UNIDAD</button>
                  {canManage && (
                    <button type="button" disabled={pending} onClick={() => applyUpdate({
                      stage: selected.stage,
                      technician_name: technician || selected.technician_name,
                      workshop_name: workshop || selected.workshop_name,
                      notes: notes || selected.notes,
                      actual_cost: Number(actualCost || selected.actual_cost || 0),
                      event_note: "Datos administrativos de la orden actualizados.",
                    })}><Save size={13} /> GUARDAR</button>
                  )}
                  {canManage && selected.stage === "in_progress" && (
                    <button type="button" disabled={pending} onClick={() => applyUpdate({ stage: "waiting_parts", event_note: "Trabajo pausado por refacción pendiente." })}><PackageSearch size={13} /> ESPERAR PARTE</button>
                  )}
                  {canManage && nextAction && (
                    <button type="button" disabled={pending} onClick={() => applyUpdate({
                      stage: nextAction[0],
                      actual_cost: Number(actualCost || selected.actual_cost || 0),
                      event_note: `Orden actualizada a ${nextAction[1].toLowerCase()}.`,
                    })}><Play size={13} /> {nextAction[1]}</button>
                  )}
                </div>
              </>
            )}
          </section>
        </div>
      </aside>
    </div>
  );
}
